import request from 'utils/request';
import { PagedView } from './dictionary';
import { UserDto } from './user';
import { CourierDto } from './couriers';
import { NPODto } from './npo';
import { BranchDto } from './branches';
import { GeocodingStatusesEnum } from './relog';

export interface OrderDto {
    id: number;
    order_number: string;
    date_created: Date;
    date_delivered?: Date;
    order_status: SimpleDto;
    courier: CourierDto;
    is_return: boolean;
    mail_type?: MailType;
    order_detail: {
        order_comment: string;
        delivery_type: string;
        with_payment?: boolean;
        with_money?: boolean;
        payment_sum: string;
        delivery_sum: string;
        weight: string;
        is_payment_for_delivery?: boolean;
        client_amount?: string;
        item_count?: string;
        is_third_delivery?: boolean;
        accepted_item_count?: number;
        issue_sum?: number;
        special_marks?: string;
        collection_date?: string;
        collection_time?: string;
        courier_arrived_date?: string;
    };
    order_address: {
        id: number;
        source: OrderAddress;
        target: OrderAddress;
    };
    related_orders?: RelatedOrdersDto[];
    acceptance_orders?: OrderItemsDto[];
    region: SimpleDto;
    branch?: BranchDto;
    product: SimpleDto;
    tenant: TenantDto;
    customers: CustomerDto[];
    newspapers: NewsPapersDto[];
    delivery_item: DeliveryItem;
    geozone_name: string;
    geocoding_status_code: GeocodingStatusesEnum;
    isGeozoneFined?: boolean;
    date_plan_delivery_period_start: Date;
    date_plan_delivery_period_end: Date;
}

export interface OrderAddress {
    id: number;
    region: SimpleDto;
    latitude: number;
    longitude: number;
    address: string;
    postal_code: string;
    new_postal_code: string;
    order_comment?: string;
}

export interface CustomerDto {
    id: number;
    customer: CustomerItemDto;
    relation_type: string;
}

export interface NewsPapersDto {
    order_id: number;
    name: string;
    amount: number;
    edition_number: number;
}

export interface DeliveryItem {
    code: string;
    description: string;
    status: number;
    is_mass: boolean;
    is_cancel: boolean;
}

export interface CustomerItemDto {
    id: number;
    name: string;
    full_name: string;
    surname?: string;
    patronymic?: string;
    phone_number?: string;
    email?: string;
    status: string;
}

interface OrderItemsDto {
    id: number;
    order_number: string;
}

interface SimpleDto {
    code: string;
    description: string;
    status: string;
}

interface RelatedOrdersDto {
    id?: number;
    order_number?: string;
    internet_number: string;
}

export interface TenantDto {
    username: string;
    first_name: string;
    last_name: string;
    enabled: boolean;
}

export interface OrderParams {
    courier_id: number | null | undefined;
    date_from: string | null;
    date_until: string | null;
    status_id: number | string;
    page: number;
    size: number;
    region_id: number | string;
    product_id: number | string;
    value?: string;
    mail_type_id: number | string;
    order_number?: string;
    branch_id?: string | number;
    client_type?: string;
    order_by_parameter?: string;
    geocoding_status_code?: GeocodingStatusesEnum;
    geozone_name?: string | null;
}

export interface OrderAction {
    id: number;
    code: string;
    description: string;
    status: string;
    is_comment_required: boolean;
}

export interface MailType {
    code: string;
    description: string;
}

export function getPaged(data: OrderParams): Promise<PagedView<OrderDto>> {
    return request({
        url: '/order/list',
        data: {
            ...data,
            client_type: 'WEB',
        },
        method: 'POST',
    }).then((response) => {
        const { data, headers } = response;
        const res: PagedView<OrderDto> = {
            content: data as OrderDto[],
            total_elements: Number(headers['x-total-count']),
        };

        return res;
    });
}

export function getGeozoneList(data: OrderParams): Promise<string[]> {
    return request({
        url: '/order/geozone-list',
        data: {
            ...data,
            client_type: 'WEB',
        },
        method: 'POST',
    }).then((response) => {
        const { data = [] } = response;
        return data;
    });
}

export function getPagedByDelivery(
    deliveryId: number | string,
    page: number,
    size: number
): Promise<PagedView<OrderDto>> {
    return request({
        url: '/delivery-list/orders',
        data: { delivery_list_id: deliveryId, page, size },
        method: 'POST',
    }).then((response) => {
        const { data, headers } = response;
        const res: PagedView<OrderDto> = {
            content: data as OrderDto[],
            total_elements: Number(headers['x-total-count']),
        };

        return res;
    });
}

export function fetchActions(
    statusCode: string | undefined,
    productId: number | string
): Promise<OrderAction[]> {
    return request(`/action/product/${statusCode}/${productId}`).then(
        ({ data }) => data as OrderAction[]
    );
}

export function fetchRequestDocuments(id: number | string): Promise<OrderDocument[]> {
    return request(`/event-task-detail/documents/${id}`).then((res) => res.data as OrderDocument[]);
}

export function fetchOrderTasks(statusCode: string, orderId: number): Promise<OrderActionItem[]> {
    return request(`/order/change-status/${statusCode}/${orderId}`).then(
        (res) => res.data as OrderActionItem[]
    );
}

export function postTransitionTask(
    orderId: number,
    taskId: number,
    ids: number[],
    courier_id: number | undefined = undefined
): Promise<TransitionTaskResponse> {
    return request('/transition-task', {
        method: 'POST',
        headers: {
            'x-order-id': orderId,
            'x-transition-task': taskId,
        },
        data: { ids, courier_id },
    }).then((res) => res.data as TransitionTaskResponse);
}

export interface TransitionTaskResponse {
    success: boolean;
}

export interface OrderActionItem {
    id: number;
    code: string;
    description: string;
    priority: number;
    event_task: EventTask;
}

export interface EventTask {
    id: number;
    code: string;
    class_name: string;
    description: string;
    is_mobile: boolean;
}

export interface OrderUpdateDto {
    status: string;
    id: number | null;
    comment: string | null;
    ids: number[];
}

export interface OrderHistoryDto {
    id: number;
    date_created: Date;
    order_status: SimpleDto;
    user: UserDto;
}

export interface OrderNPODto {
    order_number: string;
    date_created: Date;
    courier: string;
    sender: string;
    region: string;
    npos: NPODto[];
}

export interface OrderCommentDto {
    id: number;
    body: string;
    date_created: Date;
    user: UserDto;
}

export interface OrderDocument {
    id: number;
    priority: number;
    description: string;
    event_task_details: OrderEventDetail[];
}

export interface OrderEventDetail {
    id: number;
    date_created: Date | string;
    asset: OrderAsset;
    type: string;
    value: string;
    is_deleted: false;
    transition_task_id: number;
}

export interface OrderAsset {
    id: number;
    uploaded_date: Date | string;
    hash: string;
    original_file_name: string;
    size: number;
    type: string;
    content_type: string;
    extension: string;
    file_name: string;
}

export function updateStatus(data: OrderUpdateDto): Promise<any> {
    const { id, ids, comment, status } = data;
    let url = `/order/change-status/${status}`;
    let body: any = { ids };

    if (id) {
        url = `/order/change-status/${status}/${id}`;
        body = null;
    }

    return request({ url, data: body, method: 'PUT', params: { comment } });
}

export interface OrderDataUpdatePut {
    id: number;
    region_code: string;
    weight: string;
    source_address: {
        postcode?: string;
        address_rus?: string;
        full_address: {
            old_postcode?: string;
        };
    };
    source_address_dict: {
        postcode?: string;
        address_rus?: string;
        full_address: {
            old_postcode?: string;
        };
    };
    order_comment: string;
}

export function updateOrderData(data: OrderDataUpdatePut) {
    return request(`/order/change-data/${data.id}`, {
        params: data,
        method: 'PUT',
    });
}

export function updateOrderData2(data: OrderDataUpdatePut) {
    return request('/order/change-data', {
        data,
        method: 'PUT',
    });
}

export function updateOrderBranch(data: { order_id: number; branch_code: string }) {
    return request('/order/change-branch', {
        data,
        method: 'PUT',
    });
}

export function updateOrderTargetAddress(data: { order_id: number; address: string }) {
    return request('/order/change-target-address', {
        data,
        method: 'PUT',
    });
}

export function updateOrderSourceAddress(data: { order_id: number; address: string }) {
    return request('/order/change-source-address', {
        data,
        method: 'PUT',
    });
}
